import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Space,
  Input,
  Typography,
  Popconfirm,
  message,
  Tooltip,
  Descriptions,
  Spin,
} from "antd";
import { KeyOutlined, UserDeleteOutlined, UserAddOutlined } from "@ant-design/icons";
import {
  getVendorUsers,
  addVendorUser,
  removeVendorUser,
  getVendorUserLoginDetails,
} from "./services/vendorApi";

const { Text } = Typography;

// Self-contained modal: owns all of its own state so that typing in the
// "Add User by Email" field only re-renders this component, not the
// parent Vendor page (which has a large table and re-rendered fully on
// every keystroke before this was split out).
const ManageUsersModal = ({ open, vendor, onClose }) => {
  const [vendorUsers, setVendorUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [addUserEmail, setAddUserEmail] = useState("");
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [removeUserLoadingId, setRemoveUserLoadingId] = useState(null);
  const [userLoginDetails, setUserLoginDetails] = useState({});
  const [userLoginLoadingId, setUserLoginLoadingId] = useState(null);

  useEffect(() => {
    if (!open || !vendor) return;
    setAddUserEmail("");
    setVendorUsers([]);
    setUserLoginDetails({});
    setUsersLoading(true);
    getVendorUsers(vendor.id)
      .then(setVendorUsers)
      .catch(() => message.error("Failed to load vendor users"))
      .finally(() => setUsersLoading(false));
  }, [open, vendor]);

  const handleAddUser = async () => {
    if (!addUserEmail.trim()) return;
    setAddUserLoading(true);
    try {
      const newUser = await addVendorUser(vendor.id, addUserEmail.trim());
      setVendorUsers((prev) => [...prev, newUser]);
      setAddUserEmail("");
      if (newUser.created) {
        // New user was auto-created — cache their credentials so Login shows immediately
        setUserLoginDetails((prev) => ({
          ...prev,
          [newUser.id]: { email: newUser.email, password: newUser.password },
        }));
        message.success(`New user created. Click Login to see credentials.`);
      } else {
        message.success("User added successfully");
      }
    } catch {
      message.error("Failed to add user");
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    setRemoveUserLoadingId(userId);
    try {
      await removeVendorUser(vendor.id, userId);
      setVendorUsers((prev) => prev.filter((u) => u.id !== userId));
      setUserLoginDetails((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      message.success("User removed");
    } catch {
      message.error("Failed to remove user");
    } finally {
      setRemoveUserLoadingId(null);
    }
  };

  const handleShowUserLogin = async (userId) => {
    // Toggle off if already showing
    if (userLoginDetails[userId]) {
      setUserLoginDetails((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      return;
    }
    setUserLoginLoadingId(userId);
    try {
      const details = await getVendorUserLoginDetails(vendor.id, userId);
      setUserLoginDetails((prev) => ({ ...prev, [userId]: details }));
    } catch {
      message.error("Failed to load login details");
    } finally {
      setUserLoginLoadingId(null);
    }
  };

  return (
    <Modal
      title={`Manage Users — ${vendor?.vendorName || ""}`}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={520}
      destroyOnClose
    >
      {usersLoading ? (
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <Spin />
        </div>
      ) : (
        <>
          {/* Current users list */}
          {vendorUsers.length === 0 ? (
            <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
              No users assigned to this vendor.
            </Text>
          ) : (
            <div style={{ marginBottom: 16 }}>
              {vendorUsers.map((user) => {
                const creds = userLoginDetails[user.id];
                return (
                  <div key={user.id} style={{ marginBottom: 8 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 12px",
                        background: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        borderRadius: creds ? "6px 6px 0 0" : 6,
                      }}
                    >
                      <div>
                        <Text strong style={{ display: "block", fontSize: 13 }}>
                          {user.name || user.email}
                        </Text>
                        {user.name && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {user.email}
                          </Text>
                        )}
                      </div>
                      <Space size={4}>
                        <Tooltip title={creds ? "Hide Login Details" : "Show Login Details"}>
                          <Button
                            size="small"
                            icon={<KeyOutlined />}
                            loading={userLoginLoadingId === user.id}
                            onClick={() => handleShowUserLogin(user.id)}
                          />
                        </Tooltip>
                        <Popconfirm
                          title="Remove this user from the vendor?"
                          onConfirm={() => handleRemoveUser(user.id)}
                          okText="Remove"
                          okType="danger"
                        >
                          <Tooltip title="Remove User">
                            <Button
                              size="small"
                              danger
                              icon={<UserDeleteOutlined />}
                              loading={removeUserLoadingId === user.id}
                            />
                          </Tooltip>
                        </Popconfirm>
                      </Space>
                    </div>
                    {creds && (
                      <Descriptions
                        column={1}
                        size="small"
                        bordered
                        style={{
                          borderTop: "none",
                          borderRadius: "0 0 6px 6px",
                          overflow: "hidden",
                        }}
                      >
                        <Descriptions.Item label="Email">{creds.email}</Descriptions.Item>
                        <Descriptions.Item label="Password">{creds.password}</Descriptions.Item>
                      </Descriptions>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Add user by email */}
          <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 14 }}>
            <Text style={{ display: "block", marginBottom: 8, fontWeight: 500, fontSize: 13 }}>
              Add User by Email
            </Text>
            <Space.Compact style={{ width: "100%" }}>
              <Input
                placeholder="user@example.com"
                value={addUserEmail}
                onChange={(e) => setAddUserEmail(e.target.value)}
                onPressEnter={handleAddUser}
                style={{ flex: 1 }}
              />
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                loading={addUserLoading}
                onClick={handleAddUser}
                disabled={!addUserEmail.trim()}
              >
                Add
              </Button>
            </Space.Compact>
            <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: "block" }}>
              If no account exists for this email, one will be created automatically. The same email can be assigned to multiple vendors.
            </Text>
          </div>
        </>
      )}
    </Modal>
  );
};

export default ManageUsersModal;
